import { useEffect, useRef, useState, useTransition } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProfilePanel from '../components/settings/ProfilePanel.jsx';
import SecurityPanel from '../components/settings/SecurityPanel.jsx';
import NotificationsPanel from '../components/settings/NotificationsPanel.jsx';
import UsersRolesPanel from '../components/settings/UsersRolesPanel.jsx';
import AutomationsPanel from '../components/settings/AutomationsPanel.jsx';
import BillingPanel from '../components/settings/BillingPanel.jsx';
import IntegrationsPanel from '../components/settings/IntegrationsPanel.jsx';
import {
  clearQuickBooksSyncResult,
  refreshQuickBooksSyncStatus,
  startQuickBooksSync,
  useQuickBooksSync,
  autoSyncRowText,
} from '../lib/quickBooksSync.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useSaveToast } from '../components/SaveToast.jsx';
import { api } from '../lib/api.js';
import {
  connectionsFromSettings,
  connectIntegrationLocal,
  defaultSettings,
  demandChoiceFields,
  disconnectIntegrationLocal,
  normalizeDemandDefaults,
  ensureDemoBillingSeed,
  formatAddress,
  getLocalSettings,
  mergeSettings,
  orgRoleForEmail,
  roleCanOpenSettingsTab,
  saveLocalSettings,
  INTEGRATION_PRICING,
  SEAT_ADDON_PRICING,
} from '../lib/settingsStore.js';
import {
  buildEvidenceVaultUsage,
  getEvidenceVaultTiersById,
  setEvidenceVaultPlan,
} from '../lib/evidenceVault.js';
import { retainPlatformInvoices } from '../lib/platformInvoices.js';
import { listJoinedAccounts } from '../lib/joinedAccounts.js';
import { applyOrgRoster } from '../lib/orgRoster.js';
import { lookupProfilePhoto, rememberProfilePhoto } from '../lib/profilePhotos.js';
import {
  isValidUsOrCaPostalCode,
  normalizePostalCode,
  postalCodeErrorMessage,
} from '../lib/postalCode.js';

function rosterProfile(email) {
  const saved = getLocalSettings(email);
  const photo = lookupProfilePhoto(email);
  if (!photo || saved.avatarDataUrl) return saved;
  return { ...saved, avatarDataUrl: photo };
}

function rosterUsers(orgUsers, members = []) {
  return applyOrgRoster(orgUsers, {
    members,
    joined: listJoinedAccounts(),
    profileLookup: rosterProfile,
  });
}

/** Ordered by how often clients open each section (ops first, set-and-forget last). */
/** Flat list kept for tab id normalization / deep links. */
const CLIENT_TABS = [
  { id: 'billing', label: 'Billing' },
  { id: 'users', label: 'Users & Roles' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'security', label: 'Security' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'automations', label: 'Automations' },
];

/** Option B / B1: Money · Team · Connected grouping for client Account settings. */
const CLIENT_TAB_GROUPS = [
  {
    id: 'money',
    label: 'Money',
    items: [{ id: 'billing', label: 'Billing' }],
  },
  {
    id: 'team',
    label: 'Team',
    items: [
      { id: 'users', label: 'Users & Roles' },
      { id: 'notifications', label: 'Notifications' },
      { id: 'security', label: 'Security' },
    ],
  },
  {
    id: 'connected',
    label: 'Connected',
    items: [
      { id: 'integrations', label: 'Integrations' },
      { id: 'automations', label: 'Automations' },
    ],
  },
];

const PARTNER_TABS = [
  { id: 'profile', label: 'Profile' },
  { id: 'security', label: 'Security' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'integrations', label: 'Integrations' },
];

function homePathForRole(role) {
  if (role === 'ATTORNEY') return '/attorney';
  if (role === 'ADMIN') return '/admin';
  return '/client/home';
}

function normalizeTab(raw, tabs, { allowProfile = false } = {}) {
  const id = raw === 'connections' ? 'integrations' : raw;
  if (allowProfile && id === 'profile') return 'profile';
  return tabs.some((t) => t.id === id) ? id : tabs[0].id;
}

export default function SettingsPage({ embedded = false }) {
  const { user, updateSessionProfile, logout } = useAuth();
  const { flashSave, clearSaveToast } = useSaveToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const isClient = !user?.role || user.role === 'CLIENT' || embedded;
  const orgRole = orgRoleForEmail(user?.email || '');
  const clientTabs = CLIENT_TABS.filter((item) => roleCanOpenSettingsTab(orgRole, item.id));
  const tabs = isClient
    ? clientTabs.length
      ? clientTabs
      : [{ id: 'notifications', label: 'Notifications' }]
    : PARTNER_TABS;
  const clientTabGroups = CLIENT_TAB_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => roleCanOpenSettingsTab(orgRole, item.id)),
  })).filter((group) => group.items.length);
  // Client Profile lives in the account menu, not the Settings sidebar.
  const tab = normalizeTab(searchParams.get('tab'), tabs, { allowProfile: isClient });

  const [settings, setSettings] = useState(() => getLocalSettings(user?.email || ''));
  const [connections, setConnections] = useState(() =>
    connectionsFromSettings(getLocalSettings(user?.email || ''))
  );
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    avatarDataUrl: '',
    organizationName: '',
    countryRegion: 'United States',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressPostalCode: '',
    phoneNumber: '',
    currency: 'USD',
    language: 'en-US',
    licensedStates: '',
  });
  const [connectProvider, setConnectProvider] = useState(null);
  const [connectMessage, setConnectMessage] = useState('');
  // QuickBooks sync runs in an app-level store so it survives leaving Settings.
  const qbSync = useQuickBooksSync(user?.email);
  const seenSyncFinish = useRef(qbSync.finishedAt);
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();
  const [canManageMessageSharing, setCanManageMessageSharing] = useState(() => {
    const joined = getLocalSettings(user?.email || '').joinedOrganization;
    if (!joined) return true;
    const role = String(joined.role || '').toUpperCase();
    return role === 'OWNER' || role === 'ADMIN';
  });

  // Live byte usage is shown on Evidence Locker; Billing shows plan entitlement.
  const vaultUsage = {
    ...buildEvidenceVaultUsage({
      usedBytes: 0,
      billing: settings.billing || {},
    }),
    usedBytes: null,
  };

  function persist(partial) {
    const merged = saveLocalSettings(user.email, { ...settings, ...partial });
    setSettings(merged);
    setConnections(connectionsFromSettings(merged));
    return merged;
  }

  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;

    (async () => {
      const local = mergeSettings(user.email, {
        ...defaultSettings(user.email),
        ...getLocalSettings(user.email),
        displayName:
          getLocalSettings(user.email).displayName ||
          user.displayName ||
          '',
        organizationName:
          getLocalSettings(user.email).organizationName || user.organizationName || '',
        countryRegion:
          getLocalSettings(user.email).countryRegion ||
          user.countryRegion ||
          'United States',
        address: getLocalSettings(user.email).address || user.address || '',
        addressStreet:
          getLocalSettings(user.email).addressStreet || user.addressStreet || '',
        addressCity: getLocalSettings(user.email).addressCity || user.addressCity || '',
        addressState:
          getLocalSettings(user.email).addressState || user.addressState || '',
        addressPostalCode:
          getLocalSettings(user.email).addressPostalCode || user.addressPostalCode || '',
        phoneNumber:
          getLocalSettings(user.email).phoneNumber ||
          getLocalSettings(user.email).notifications?.phoneNumber ||
          user.phoneNumber ||
          '',
        currency: getLocalSettings(user.email).currency || user.currency || 'USD',
        language: getLocalSettings(user.email).language || user.language || 'en-US',
        licensedStates:
          user.licensedStates || getLocalSettings(user.email).licensedStates || '',
      });

      // Seed/refresh demo billing (charges age out of the 30-day window otherwise).
      local.billing = ensureDemoBillingSeed(local.billing || {});
      if (!local.avatarDataUrl) local.avatarDataUrl = lookupProfilePhoto(user.email);
      if (!local.avatarDataUrl && user.avatarDataUrl) local.avatarDataUrl = user.avatarDataUrl;
      local.orgUsers = rosterUsers(local.orgUsers);

      if (!cancelled) {
        setSettings(local);
        setProfileForm({
          displayName: local.displayName || '',
          avatarDataUrl: local.avatarDataUrl || user.avatarDataUrl || '',
          organizationName: local.organizationName || '',
          countryRegion: local.countryRegion || 'United States',
          addressStreet: local.addressStreet || '',
          addressCity: local.addressCity || '',
          addressState: local.addressState || '',
          addressPostalCode: local.addressPostalCode || '',
          phoneNumber: local.phoneNumber || local.notifications?.phoneNumber || '',
          currency: local.currency || 'USD',
          language: local.language || 'en-US',
          licensedStates: local.licensedStates || '',
        });
        setConnections(connectionsFromSettings(local));
        saveLocalSettings(user.email, local);
        if (local.avatarDataUrl && local.avatarDataUrl !== user.avatarDataUrl) {
          updateSessionProfile({ avatarDataUrl: local.avatarDataUrl });
        }
      }

      try {
        const account = await api.get(
          `/server/auth_handler/settings/account?email=${encodeURIComponent(user.email)}`
        );
        if (!cancelled && account.settings) {
          const serverUsers = Array.isArray(account.settings.orgUsers)
            ? account.settings.orgUsers
            : null;
          const serverHasTeammates = Boolean(
            serverUsers?.some((person) => person?.role !== 'OWNER')
          );
          const merged = {
            ...local,
            ...account.settings,
            orgUsers: rosterUsers(
              serverHasTeammates ? serverUsers : local.orgUsers,
              account.orgMembers
            ),
            demoTeammatesSeeded: Boolean(
              local.demoTeammatesSeeded || account.settings.demoTeammatesSeeded
            ),
            displayName: account.settings.displayName || local.displayName,
            avatarDataUrl:
              String(account.settings.avatarDataUrl || '').trim() ||
              lookupProfilePhoto(user.email) ||
              getLocalSettings(user.email).avatarDataUrl ||
              local.avatarDataUrl ||
              user.avatarDataUrl ||
              '',
            phoneNumber:
              account.settings.phoneNumber ||
              account.settings.notifications?.phoneNumber ||
              local.phoneNumber ||
              '',
            joinedOrganization: account.organization || null,
            shareOrganizationMessages:
              typeof account.shareOrganizationMessages === 'boolean'
                ? account.shareOrganizationMessages
                : account.settings.shareOrganizationMessages === true,
          };
          if (merged.avatarDataUrl) rememberProfilePhoto(user.email, merged.avatarDataUrl);
          if (
            merged.avatarDataUrl &&
            !String(account.settings.avatarDataUrl || '').trim()
          ) {
            void api
              .put('/server/auth_handler/settings/profile', {
                email: user.email,
                displayName: merged.displayName || '',
                avatarDataUrl: merged.avatarDataUrl,
              })
              .catch(() => {});
          }
          // Server settings can overwrite demo billing with empty/stale payments.
          merged.billing = ensureDemoBillingSeed(merged.billing || local.billing || {});
          const next = saveLocalSettings(user.email, merged);
          setSettings(next);
          setProfileForm({
            displayName: next.displayName || '',
            avatarDataUrl: next.avatarDataUrl || local.avatarDataUrl || user.avatarDataUrl || '',
            organizationName: next.organizationName || '',
            countryRegion: next.countryRegion || 'United States',
            addressStreet: next.addressStreet || '',
            addressCity: next.addressCity || '',
            addressState: next.addressState || '',
            addressPostalCode: next.addressPostalCode || '',
            phoneNumber: next.phoneNumber || next.notifications?.phoneNumber || '',
            currency: next.currency || 'USD',
            language: next.language || 'en-US',
            licensedStates: next.licensedStates || '',
          });
          setConnections(connectionsFromSettings(next));
          if (typeof account.canManageMessageSharing === 'boolean') {
            setCanManageMessageSharing(account.canManageMessageSharing);
          }
        }
      } catch {
        try {
          const qb = await api.get(
            `/server/quickbooks_api/status?email=${encodeURIComponent(user.email)}`
          );
          if (!cancelled && qb.connection?.status === 'connected') {
            const next = saveLocalSettings(user.email, {
              ...local,
              accountingProvider: 'QUICKBOOKS',
              accountingConnected: true,
              accountingConnectedAt: qb.connection.connectedAt || local.accountingConnectedAt,
              accountingRealmId: qb.connection.realmId || local.accountingRealmId,
              accountingLastSyncAt: qb.connection.lastSyncAt || local.accountingLastSyncAt,
              accountingDemo: Boolean(qb.connection.demo),
            });
            setSettings(next);
            setConnections(connectionsFromSettings(next));
          }
        } catch {
          // local only
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.email, user?.displayName, user?.licensedStates]);

  useEffect(() => {
    if (!user?.email) return undefined;
    let timer = 0;
    let cancelled = false;
    const refreshRoster = () => {
      if (document.visibilityState === 'hidden') return;
      window.clearTimeout(timer);
      timer = window.setTimeout(async () => {
        try {
          const account = await api.get(
            `/server/auth_handler/settings/account?email=${encodeURIComponent(user.email)}`
          );
          if (cancelled || !account?.settings) return;
          const serverUsers = Array.isArray(account.settings.orgUsers)
            ? account.settings.orgUsers
            : null;
          setSettings((prev) => {
            const orgUsers = rosterUsers(
              serverUsers?.some((person) => person?.role !== 'OWNER')
                ? serverUsers
                : prev.orgUsers,
              account.orgMembers
            );
            const next = { ...prev, orgUsers };
            saveLocalSettings(user.email, next);
            return next;
          });
        } catch {
          if (cancelled) return;
          setSettings((prev) => {
            const orgUsers = rosterUsers(prev.orgUsers);
            return { ...prev, orgUsers };
          });
        }
      }, 250);
    };
    document.addEventListener('visibilitychange', refreshRoster);
    window.addEventListener('focus', refreshRoster);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', refreshRoster);
      window.removeEventListener('focus', refreshRoster);
    };
  }, [user?.email]);

  useEffect(() => {
    const qb = searchParams.get('qb');
    const connected = searchParams.get('connected');
    const connectError = searchParams.get('connect_error');
    const demo = searchParams.get('demo') === '1';

    if (!qb && !connected && !connectError) return;

    if (connectError) {
      setError(connectError);
      clearSaveToast();
      setSearchParams({ tab: 'integrations' }, { replace: true });
      return;
    }

    if (!user?.email) return;

    const providerId = String(connected || (qb === 'connected' ? 'QUICKBOOKS' : '')).toUpperCase();
    const label = providerId.replace(/_/g, ' ') || 'Accounting app';
    flashSave(
      demo
        ? `${label} connected in demo mode.`
        : `${label} connected. DemandJet can now import invoices from your account.`
    );
    setError('');
    setConnectProvider(null);
    setConnectMessage('');
    setSearchParams({ tab: 'integrations' }, { replace: true });

    (async () => {
      try {
        const account = await api.get(
          `/server/auth_handler/settings/account?email=${encodeURIComponent(user.email)}`
        );
        if (account.settings) {
          const next = saveLocalSettings(user.email, {
            ...getLocalSettings(user.email),
            ...account.settings,
          });
          setSettings(next);
          setConnections(connectionsFromSettings(next));
          return;
        }
      } catch {
        // fall through to local mark
      }
      if (providerId) {
        const next = connectIntegrationLocal(getLocalSettings(user.email), providerId, {
          connectedAt: new Date().toISOString(),
          demo,
          primary: true,
        });
        const saved = saveLocalSettings(user.email, next);
        setSettings(saved);
        setConnections(connectionsFromSettings(saved));
      }
    })();
  }, [searchParams, setSearchParams, user?.email]);

  const allowedSettingsTabs = tabs.map((item) => item.id).join('|');
  useEffect(() => {
    if (!isClient) return;
    const raw = searchParams.get('tab');
    if (!raw || raw === 'profile') return;
    const id = raw === 'connections' ? 'integrations' : raw;
    if (roleCanOpenSettingsTab(orgRole, id)) return;
    setTab(tabs[0]?.id || 'notifications');
    // allowedSettingsTabs is the stable stand-in for the filtered tab list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedSettingsTabs, isClient, orgRole, searchParams]);

  function setTab(next) {
    const defaultTab = tabs[0]?.id;
    if (next === 'profile') {
      setSearchParams({ tab: 'profile' });
    } else if (next === defaultTab) {
      setSearchParams({});
    } else {
      setSearchParams({ tab: next });
    }
    clearSaveToast();
    setError('');
    setConnectProvider(null);
    setConnectMessage('');
    clearQuickBooksSyncResult();
  }

  function onShareMessages(shared) {
    const previous = settings.shareOrganizationMessages === true;
    const next = persist({ shareOrganizationMessages: shared });
    clearSaveToast();
    setError('');
    startTransition(() => {
      api
        .put('/server/client_api/message-sharing', { shared })
        .then(() => {
          flashSave(
            shared
              ? 'Organization messages are shared.'
              : 'Each person sees the messages they sent.'
          );
        })
        .catch((err) => {
          if (err?.status === 403) {
            const reverted = saveLocalSettings(user.email, {
              ...next,
              shareOrganizationMessages: previous,
            });
            setSettings(reverted);
            setError(err.message || 'Only an owner or admin can change message sharing.');
            return;
          }
          flashSave('Message sharing saved locally (API offline).');
        });
    });
  }

  function saveSection(label, patch, options = {}) {
    clearSaveToast();
    setError('');
    return new Promise((resolve) => {
      startTransition(() => {
        const next = persist(patch);
        const messageFor = (offline) =>
          offline ? `${label} saved locally (API offline).` : `${label} saved.`;
        api
          .put('/server/auth_handler/settings/account', {
            email: user.email,
            settings: next,
          })
          .then(() => {
            const message = messageFor(false);
            if (!options.quiet) flashSave(message);
            resolve(message);
          })
          .catch(() => {
            const message = messageFor(true);
            if (!options.quiet) flashSave(message);
            resolve(message);
          });
      });
    });
  }

  async function postTeammateInvite(invite) {
    await api.post('/server/auth_handler/settings/invites', {
      email: invite.email,
      name: invite.name,
      role: invite.role,
    });
  }

  async function saveAccountSettings(next) {
    try {
      await api.put('/server/auth_handler/settings/account', {
        email: user.email,
        settings: next,
      });
    } catch {
      // The invite already went out. The teammate list stays in this browser.
    }
  }

  async function sendTeammateInvite(invite) {
    clearSaveToast();
    setError('');
    await postTeammateInvite(invite);
    const orgUsers = [...settings.orgUsers, invite];
    const seatPrice = SEAT_ADDON_PRICING.extraSeatMonthly;
    const willBillExtra = orgUsers.length > 1;
    let billing = {
      ...settings.billing,
      extraSeats: Math.max(0, orgUsers.length - 1),
    };
    if (willBillExtra) {
      billing = {
        ...billing,
        invoices: retainPlatformInvoices([
          {
            id: `inv_seat_${Date.now()}`,
            label: `Extra user · ${invite.email || invite.name || 'teammate'}`,
            amount: seatPrice,
            currency: 'USD',
            status: 'OPEN',
            issuedAt: new Date().toISOString(),
          },
          ...(billing.invoices || []),
        ]),
      };
    }
    const next = persist({ orgUsers, billing });
    await saveAccountSettings(next);
    flashSave(
      willBillExtra
        ? `Invite emailed to ${invite.email}. Extra-user add-on billed at $${seatPrice}/mo.`
        : `Invite emailed to ${invite.email}.`
    );
  }

  async function resendTeammateInvite(invite) {
    clearSaveToast();
    setError('');
    await postTeammateInvite(invite);
    const orgUsers = settings.orgUsers.map((person) =>
      person.id === invite.id
        ? {
            ...person,
            status: person.status === 'ACTIVE' ? 'ACTIVE' : 'INVITED',
            invitedAt: new Date().toISOString(),
          }
        : person
    );
    const next = persist({ orgUsers });
    await saveAccountSettings(next);
    flashSave(`Invite emailed again to ${invite.email}.`);
  }

  async function changePassword({ newPassword }) {
    clearSaveToast();
    setError('');
    try {
      await api.post('/server/auth_handler/settings/password', {
        newPassword,
      });
    } catch (err) {
      const offline = !err?.status;
      return {
        ok: false,
        message: offline
          ? 'Could not reach the server. Check your connection and try again.'
          : err.message || 'Unable to update password.',
      };
    }
    const next = persist({
      security: { ...settings.security, lastPasswordChangeAt: new Date().toISOString() },
    });
    try {
      await api.put('/server/auth_handler/settings/account', {
        email: user.email,
        settings: next,
      });
    } catch {
      /* password already changed; timestamp is best-effort */
    }
    flashSave('Password updated.');
    return { ok: true };
  }

  function saveProfile(e) {
    e.preventDefault();
    clearSaveToast();
    setError('');
    const addressPostalCode = normalizePostalCode(profileForm.addressPostalCode);
    if (addressPostalCode !== profileForm.addressPostalCode) {
      setProfileForm((prev) => ({ ...prev, addressPostalCode }));
    }
    if (!isValidUsOrCaPostalCode(addressPostalCode)) {
      setError(postalCodeErrorMessage(addressPostalCode));
      return;
    }
    startTransition(async () => {
      const composedAddress = formatAddress({
        addressStreet: profileForm.addressStreet,
        addressCity: profileForm.addressCity,
        addressState: profileForm.addressState,
        addressPostalCode,
      });
      const payload = {
        email: user.email,
        displayName: profileForm.displayName.trim(),
        Display_Name: profileForm.displayName.trim(),
        organizationName: profileForm.organizationName.trim(),
        Organization_Name: profileForm.organizationName.trim(),
        countryRegion: profileForm.countryRegion.trim(),
        Country_Region: profileForm.countryRegion.trim(),
        addressStreet: profileForm.addressStreet.trim(),
        Address_Street: profileForm.addressStreet.trim(),
        addressCity: profileForm.addressCity.trim(),
        Address_City: profileForm.addressCity.trim(),
        addressState: profileForm.addressState.trim(),
        Address_State: profileForm.addressState.trim(),
        addressPostalCode,
        Address_Postal_Code: addressPostalCode,
        address: composedAddress,
        Address: composedAddress,
        currency: profileForm.currency,
        Currency: profileForm.currency,
        language: profileForm.language,
        Language: profileForm.language,
        phoneNumber: profileForm.phoneNumber.trim(),
        Phone_Number: profileForm.phoneNumber.trim(),
        Notification_Phone: profileForm.phoneNumber.trim(),
        licensedStates: profileForm.licensedStates.trim().toUpperCase(),
        Licensed_States: profileForm.licensedStates.trim().toUpperCase(),
        avatarDataUrl: profileForm.avatarDataUrl || '',
      };

      if (payload.avatarDataUrl) rememberProfilePhoto(user.email, payload.avatarDataUrl);
      else rememberProfilePhoto(user.email, '');

      const localPatch = {
        displayName: payload.displayName,
        avatarDataUrl: profileForm.avatarDataUrl || '',
        organizationName: payload.organizationName,
        countryRegion: payload.countryRegion,
        addressStreet: payload.addressStreet,
        addressCity: payload.addressCity,
        addressState: payload.addressState,
        addressPostalCode: payload.addressPostalCode,
        address: payload.address,
        phoneNumber: payload.phoneNumber,
        currency: payload.currency,
        language: payload.language,
        licensedStates: payload.licensedStates,
        notifications: {
          ...(settings.notifications || {}),
          phoneNumber: payload.phoneNumber,
        },
      };

      try {
        const data = await api.put('/server/auth_handler/settings/profile', payload);
        const profile = data.profile || {};
        const next = persist({
          ...localPatch,
          displayName: profile.Display_Name || payload.displayName,
          organizationName: profile.Organization_Name || payload.organizationName,
          countryRegion: profile.Country_Region || payload.countryRegion,
          addressStreet: profile.Address_Street || payload.addressStreet,
          addressCity: profile.Address_City || payload.addressCity,
          addressState: profile.Address_State || payload.addressState,
          addressPostalCode: profile.Address_Postal_Code || payload.addressPostalCode,
          address: profile.Address || payload.address,
          phoneNumber:
            profile.Notification_Phone || profile.Phone_Number || payload.phoneNumber,
          currency: profile.Currency || payload.currency,
          language: profile.Language || payload.language,
          licensedStates: profile.Licensed_States || payload.licensedStates,
          notifications: {
            ...(settings.notifications || {}),
            phoneNumber:
              profile.Notification_Phone || profile.Phone_Number || payload.phoneNumber,
          },
        });
        updateSessionProfile({
          displayName: next.displayName,
          licensedStates: next.licensedStates,
          phoneNumber: next.phoneNumber,
          avatarDataUrl: next.avatarDataUrl || lookupProfilePhoto(user.email) || '',
        });
        flashSave('Profile saved.');
      } catch {
        const next = persist(localPatch);
        updateSessionProfile({
          displayName: next.displayName,
          licensedStates: next.licensedStates,
          phoneNumber: next.phoneNumber,
          avatarDataUrl: next.avatarDataUrl || lookupProfilePhoto(user.email) || '',
        });
        flashSave('Profile saved locally (API offline).');
      }
    });
  }

  function withIntegrationInvoice(partialSettings, providerLabel) {
    const amount = INTEGRATION_PRICING.connectionMonthly;
    const billingBase = partialSettings.billing || settings.billing || {};
    return {
      ...partialSettings,
      billing: {
        ...billingBase,
        invoices: retainPlatformInvoices([
          {
            id: `inv_integration_${Date.now()}`,
            label: `Integration · ${providerLabel}`,
            amount,
            currency: 'USD',
            status: 'OPEN',
            issuedAt: new Date().toISOString(),
          },
          ...(billingBase.invoices || []),
        ]),
      },
    };
  }

  function applyQuickBooksConnection(connection, extras = {}) {
    const patched = connectIntegrationLocal(settings, 'QUICKBOOKS', {
      connectedAt: connection.connectedAt || new Date().toISOString(),
      realmId: connection.realmId || extras.realmId || 'demo-realm',
      lastSyncAt: connection.lastSyncAt || settings.accountingLastSyncAt,
      demo: Boolean(connection.demo || extras.demo),
      authToken: extras.token || settings.accountingAuthToken,
      primary: true,
    });
    return persist(withIntegrationInvoice(patched, 'QuickBooks'));
  }

  function openConnect(providerId) {
    setError('');
    clearSaveToast();
    setConnectMessage(
      `Continue to sign in at your ${String(providerId).replace(/_/g, ' ')} account and grant DemandJet access.`
    );
    setConnectProvider(providerId);
  }

  function continueOAuth() {
    if (!connectProvider) return;
    setError('');
    clearSaveToast();
    setConnectMessage('Opening your accounting provider…');

    startTransition(async () => {
      try {
        const frontend = encodeURIComponent(window.location.origin);
        const path =
          connectProvider === 'QUICKBOOKS'
            ? `/server/quickbooks_api/oauth/start?email=${encodeURIComponent(user.email)}&frontend=${frontend}`
            : `/server/auth_handler/settings/connections/oauth/start?provider=${encodeURIComponent(
                connectProvider
              )}&email=${encodeURIComponent(user.email)}&frontend=${frontend}`;
        const start = await api.get(path);
        if (start.authorizeUrl) {
          window.location.assign(start.authorizeUrl);
          return;
        }
        setConnectMessage(
          start.message ||
            `${String(connectProvider).replace(/_/g, ' ')} OAuth is not configured yet. You can use a demo connection for local testing, or ask an admin to add provider client credentials.`
        );
      } catch (err) {
        setConnectMessage(
          err.message ||
            'Could not start OAuth. Check that the API is online, or use a demo connection.'
        );
      }
    });
  }

  function connectWithDemo() {
    if (!connectProvider) return;
    setError('');
    clearSaveToast();

    startTransition(async () => {
      try {
        if (connectProvider === 'QUICKBOOKS') {
          const data = await api.post('/server/quickbooks_api/demo/connect', {
            email: user.email,
            realmId: 'demo-realm',
          });
          applyQuickBooksConnection(data.connection || {}, { demo: true });
          flashSave(
            'QuickBooks connected in demo mode. Use Sync invoices to import open invoices.'
          );
        } else {
          const data = await api.post('/server/auth_handler/settings/connections/connect', {
            email: user.email,
            provider: connectProvider,
            demo: true,
          });
          const patched = connectIntegrationLocal(settings, connectProvider, {
            connectedAt: data.connection?.connectedAt || new Date().toISOString(),
            realmId: data.connection?.realmId || 'demo-realm',
            demo: true,
            primary: true,
          });
          const label = data.connection?.name || connectProvider.replace(/_/g, ' ');
          persist(withIntegrationInvoice(patched, label));
          flashSave(`${label} connected (demo).`);
        }
        setConnectProvider(null);
        setConnectMessage('');
      } catch {
        if (connectProvider === 'QUICKBOOKS') {
          applyQuickBooksConnection(
            {
              connectedAt: new Date().toISOString(),
              realmId: 'demo-realm',
              demo: true,
            },
            { demo: true, token: `qb_demo_access_${Date.now()}` }
          );
          flashSave(
            'QuickBooks connected locally in demo mode (API offline). You can still sync sample invoices.'
          );
        } else {
          const patched = connectIntegrationLocal(settings, connectProvider, {
            connectedAt: new Date().toISOString(),
            demo: true,
            primary: true,
          });
          const label = connectProvider.replace(/_/g, ' ');
          persist(withIntegrationInvoice(patched, label));
          flashSave(`${label} connected locally (API offline).`);
        }
        setConnectProvider(null);
        setConnectMessage('');
      }
    });
  }

  function disconnect(providerId) {
    setError('');
    clearSaveToast();
    clearQuickBooksSyncResult();
    startTransition(async () => {
      if (providerId === 'QUICKBOOKS') {
        try {
          await api.post('/server/quickbooks_api/disconnect', { email: user.email });
        } catch {
          // not implemented yet; the generic disconnect below still clears the connection
        }
      }
      try {
        await api.post('/server/auth_handler/settings/connections/disconnect', {
          email: user.email,
          provider: providerId,
        });
      } catch {
        // local disconnect
      }
      const patched = disconnectIntegrationLocal(settings, providerId);
      persist(patched);
      const label =
        connections.find((c) => c.id === providerId)?.name ||
        providerId.replace(/_/g, ' ');
      flashSave(`${label} disconnected.`);
    });
  }

  // Pick up a background sync that is running, or finished while away.
  useEffect(() => {
    if (user?.email) refreshQuickBooksSyncStatus(user.email);
  }, [user?.email]);

  // A sync that finishes while this page is open: show the new Last sync time.
  useEffect(() => {
    if (!qbSync.finishedAt || qbSync.finishedAt === seenSyncFinish.current) return;
    seenSyncFinish.current = qbSync.finishedAt;
    const next = getLocalSettings(user.email);
    setSettings(next);
    setConnections(connectionsFromSettings(next));
    if (qbSync.notice?.tone === 'success') flashSave(qbSync.notice.text);
  }, [qbSync.finishedAt]);

  function syncQuickBooks(syncChoice) {
    setError('');
    clearSaveToast();
    startQuickBooksSync({
      email: user.email,
      syncChoice,
      demandChoices: demandChoiceFields(settings),
    });
  }

  function renderIntegrations() {
    return (
      <IntegrationsPanel
        connections={connections}
        connectProvider={connectProvider}
        connectMessage={connectMessage}
        onOpenConnect={openConnect}
        onContinueOAuth={continueOAuth}
        onDemoConnect={connectWithDemo}
        onCancelConnect={() => {
          setConnectProvider(null);
          setConnectMessage('');
        }}
        onDisconnect={disconnect}
        onSyncQuickBooks={syncQuickBooks}
        syncResult={qbSync.result}
        syncNotice={qbSync.notice}
        syncing={qbSync.syncing}
        syncProgress={qbSync.progress}
        syncChoice={settings.quickBooksSyncChoice}
        autoSyncText={autoSyncRowText(settings.automations, qbSync.lastAutoSync)}
        pending={pending}
      />
    );
  }

  const pageHead = (
    <div className="section-head">
      <div>
        <h1>{isClient && tab === 'profile' ? 'Profile' : 'Account settings'}</h1>
        {isClient && tab === 'profile' ? (
          <p>Your display name, organization, and contact details.</p>
        ) : isClient ? (
          <p>Money, team, and connected tools for your organization.</p>
        ) : null}
      </div>
    </div>
  );

  const settingsBody = (
        <div
          className={[
            'settings-layout',
            isClient && tab === 'profile' ? 'is-profile-only' : '',
            isClient ? 'settings-layout--grouped' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {!(isClient && tab === 'profile') ? (
            <nav
              className={`settings-nav${isClient ? ' settings-nav--grouped' : ''}`}
              aria-label="Settings sections"
            >
              {isClient ? (
                <>
                  <label className="settings-nav-select-wrap" htmlFor="settings-section-select">
                    <span className="settings-nav-select-label">Section</span>
                    <select
                      id="settings-section-select"
                      className="settings-nav-select"
                      value={tab}
                      onChange={(event) => setTab(event.target.value)}
                    >
                      {clientTabGroups.map((group) => (
                        <optgroup key={group.id} label={group.label}>
                          {group.items.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item.label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                  <div className="settings-nav-groups">
                    {clientTabGroups.map((group) => (
                      <div key={group.id} className="settings-nav-group">
                        <div className="settings-nav-group-label">{group.label}</div>
                        {group.items.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className={
                              tab === item.id ? 'settings-nav-item active' : 'settings-nav-item'
                            }
                            onClick={() => setTab(item.id)}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                tabs.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={tab === item.id ? 'settings-nav-item active' : 'settings-nav-item'}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </button>
                ))
              )}
            </nav>
          ) : null}

          <section className="settings-panel">
            {error ? <p className="form-error">{error}</p> : null}

            {tab === 'profile' ? (
              <ProfilePanel
                user={user}
                form={profileForm}
                onChange={(patch) => setProfileForm((prev) => ({ ...prev, ...patch }))}
                onSubmit={saveProfile}
                pending={pending}
              />
            ) : null}

            {tab === 'security' ? (
              <SecurityPanel
                security={settings.security}
                onChange={(patch) =>
                  setSettings((prev) => ({
                    ...prev,
                    security: { ...prev.security, ...patch },
                  }))
                }
                onSave={() =>
                  saveSection('Security settings', { security: settings.security }, { quiet: true })
                }
                onMfaUpdated={(patch) =>
                  persist({ security: { ...settings.security, ...patch } })
                }
                onChangePassword={changePassword}
                pending={pending}
              />
            ) : null}

            {tab === 'notifications' ? (
              <NotificationsPanel
                notifications={settings.notifications}
                onChange={(patch) =>
                  setSettings((prev) => ({
                    ...prev,
                    notifications: { ...prev.notifications, ...patch },
                  }))
                }
                onPipelineChange={(id, checked) =>
                  setSettings((prev) => ({
                    ...prev,
                    notifications: {
                      ...prev.notifications,
                      pipeline: { ...prev.notifications.pipeline, [id]: checked },
                    },
                  }))
                }
                onSubmit={() =>
                  saveSection('Notifications', { notifications: settings.notifications })
                }
                pending={pending}
              />
            ) : null}

            {tab === 'integrations' ? renderIntegrations() : null}

            {tab === 'users' ? (
              <UsersRolesPanel
                orgUsers={
                  settings.joinedOrganization?.orgUsers?.length
                    ? settings.joinedOrganization.orgUsers
                    : settings.orgUsers
                }
                canManage={!settings.joinedOrganization}
                shareMessages={settings.shareOrganizationMessages === true}
                canManageSharing={canManageMessageSharing}
                onShareMessages={onShareMessages}
                accountEmail={user?.email || settings.email}
                accountAvatar={settings.avatarDataUrl || user?.avatarDataUrl || ''}
                onInvite={(invite) => sendTeammateInvite(invite)}
                onResend={(invite) => resendTeammateInvite(invite)}
                onUpdateRole={(id, role) => {
                  const orgUsers = settings.orgUsers.map((u) =>
                    u.id === id ? { ...u, role } : u
                  );
                  setSettings((prev) => ({ ...prev, orgUsers }));
                  saveSection('User role', { orgUsers });
                }}
                onRemove={(id) => {
                  const orgUsers = settings.orgUsers.filter((u) => u.id !== id);
                  const billing = {
                    ...settings.billing,
                    extraSeats: Math.max(0, orgUsers.length - 1),
                  };
                  setSettings((prev) => ({ ...prev, orgUsers, billing }));
                  saveSection('User removed', { orgUsers, billing });
                }}
                pending={pending}
              />
            ) : null}

            {tab === 'automations' ? (
              <AutomationsPanel
                automations={settings.automations}
                demandDefaults={settings.demandDefaults}
                onDemandDefaultChange={(patch) => {
                  const demandDefaults = normalizeDemandDefaults({
                    ...settings.demandDefaults,
                    ...patch,
                  });
                  saveSection('Demand defaults', { demandDefaults });
                }}
                onChange={(patch) =>
                  setSettings((prev) => ({
                    ...prev,
                    automations: { ...prev.automations, ...patch },
                  }))
                }
                onSubmit={() =>
                  saveSection('Automations', {
                    automations: settings.automations,
                  })
                }
                pending={pending}
              />
            ) : null}

            {tab === 'billing' ? (
              <BillingPanel
                billing={settings.billing}
                vaultUsage={vaultUsage}
                connections={connections}
                orgUsers={settings.orgUsers}
                onChange={(patch) =>
                  setSettings((prev) => ({
                    ...prev,
                    billing: { ...prev.billing, ...patch },
                  }))
                }
                onAddPaymentMethod={() => {
                  const pm = {
                    id: `pm_${Date.now()}`,
                    brand: 'Visa',
                    last4: String(1000 + Math.floor(Math.random() * 9000)).slice(-4),
                    expMonth: 6,
                    expYear: 2030,
                    isDefault: !(settings.billing.paymentMethods || []).length,
                  };
                  const paymentMethods = [...(settings.billing.paymentMethods || []), pm];
                  const billing = { ...settings.billing, paymentMethods };
                  setSettings((prev) => ({ ...prev, billing }));
                  saveSection('Payment method', { billing });
                }}
                onSetDefaultPaymentMethod={(paymentMethodId) => {
                  const paymentMethods = (settings.billing.paymentMethods || []).map((pm) => ({
                    ...pm,
                    isDefault: pm.id === paymentMethodId,
                  }));
                  const billing = { ...settings.billing, paymentMethods };
                  setSettings((prev) => ({ ...prev, billing }));
                  saveSection('Default payment method', { billing });
                  flashSave('Default payment method updated.');
                }}
                onRemovePaymentMethod={(paymentMethodId) => {
                  const remaining = (settings.billing.paymentMethods || []).filter(
                    (pm) => pm.id !== paymentMethodId
                  );
                  const hadDefault = (settings.billing.paymentMethods || []).some(
                    (pm) => pm.id === paymentMethodId && pm.isDefault
                  );
                  const paymentMethods =
                    hadDefault && remaining.length
                      ? remaining.map((pm, index) => ({ ...pm, isDefault: index === 0 }))
                      : remaining;
                  const billing = { ...settings.billing, paymentMethods };
                  setSettings((prev) => ({ ...prev, billing }));
                  saveSection('Removed payment method', { billing });
                  flashSave('Payment method removed.');
                }}
                onSelectVaultPlan={(planId) => {
                  const tier = getEvidenceVaultTiersById(planId);
                  const billing = setEvidenceVaultPlan(settings.billing, planId, {
                    stripeItemId:
                      planId === 'included'
                        ? null
                        : settings.billing.evidenceVaultStripeItemId ||
                          `si_demo_vault_${planId}_${Date.now()}`,
                    stripeSubscriptionId:
                      planId === 'included'
                        ? null
                        : settings.billing.evidenceVaultStripeSubscriptionId ||
                          `sub_demo_vault_${Date.now()}`,
                  });
                  let invoices = billing.invoices || [];
                  if (tier.monthlyPrice > 0) {
                    invoices = retainPlatformInvoices([
                      {
                        id: `inv_vault_${planId}_${Date.now()}`,
                        label: `Evidence Locker · ${tier.label}`,
                        amount: tier.monthlyPrice,
                        currency: 'USD',
                        status: 'OPEN',
                        issuedAt: new Date().toISOString(),
                      },
                      ...invoices,
                    ]);
                  } else {
                    invoices = retainPlatformInvoices(invoices);
                  }
                  const nextBilling = { ...billing, invoices };
                  setSettings((prev) => ({ ...prev, billing: nextBilling }));
                  saveSection(`Evidence Locker · ${tier.label}`, { billing: nextBilling });
                  flashSave(
                    planId === 'included'
                      ? 'Evidence Locker set to Included (2 GB).'
                      : `Evidence Locker set to ${tier.label} (${tier.gbLimit} GB · $${tier.monthlyPrice}/mo). Stripe Checkout can replace this demo activation.`
                  );
                }}
                pending={pending}
              />
            ) : null}
          </section>
        </div>
  );

  if (embedded) {
    return (
      <div className="client-page settings-embedded">
        {pageHead}
        {settingsBody}
      </div>
    );
  }

  return (
    <div className="app-shell">
      <header className="app-topbar">
        <div className="topbar-brand">
          <div>
            <span>Settings</span>
          </div>
        </div>
        <div className="topbar-actions">
          <Link className="btn-ghost" to={homePathForRole(user?.role)}>
            Back to workspace
          </Link>
          <button type="button" className="btn-ghost" onClick={logout}>
            Sign out
          </button>
        </div>
      </header>

      <main className="app-main settings-main">
        {pageHead}
        {settingsBody}
      </main>
    </div>
  );
}
